import React, { useState } from 'react';
import {
  arkoExactFreigeschaltet,
  deleteHintergrund,
  editHintergrundText,
  loadHintergrundMeta,
  placeAnalysisInArkoChat,
  saveHintergrundMeta,
  toggleHintergrundMark,
  type HintergrundLauf,
} from '../portalHintergrundAnalyse';
import { useOptionalPortalBackgroundJobs } from '../portalBackgroundJobsContext';

type DokumenteHintergrundAnalyseProps = {
  customerId: string;
  bausteinKategorien?: unknown;
  openId: string | null;
  laeufe: readonly HintergrundLauf[];
  onOpenId: (id: string | null) => void;
  onChange: () => void;
  onOpenArkoChat?: () => void;
};

const actionClass = 'portal-hell-hover rounded-md border border-violet-400/30 px-2 py-1 text-[11px] text-slate-200 hover:bg-white/[0.04]';

/**
 * Klasse PORTAL_HINTERGRUND_ANALYSE.
 * Inhalt liegt in diesem Untertab. Bearbeiten, Löschen und Markieren für jeden Sitz.
 */
export const DokumenteHintergrundAnalyse: React.FC<DokumenteHintergrundAnalyseProps> = ({
  customerId,
  bausteinKategorien,
  openId,
  laeufe,
  onOpenId,
  onChange,
  onOpenArkoChat,
}) => {
  const jobs = useOptionalPortalBackgroundJobs();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const chatAllowed = arkoExactFreigeschaltet(bausteinKategorien);

  const persist = (next: ReturnType<typeof loadHintergrundMeta>) => {
    saveHintergrundMeta(customerId, next);
    onChange();
  };

  return (
    <div data-testid="portal-hintergrund-analyse" className="space-y-2">
      <p className="m-0 text-[11px] text-slate-500">Hintergrund Analyse</p>
      {laeufe.length === 0 ? (
        <p className="m-0 text-[11px] text-slate-600">Keine Hintergrundanalyse.</p>
      ) : (
        laeufe.map((entry) => {
          const open = openId === entry.id;
          return (
            <article
              key={entry.id}
              data-testid="portal-hintergrund-eintrag"
              data-analyse-id={entry.id}
              data-open={open ? '1' : '0'}
              data-marked={entry.marked ? '1' : '0'}
              className="rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-2"
            >
              <button
                type="button"
                className="block w-full text-left"
                onClick={() => onOpenId(open ? null : entry.id)}
              >
                <p className="m-0 text-xs text-slate-200">{entry.documentName}</p>
                <p className="m-0 mt-0.5 text-[10px] text-slate-500">
                  {entry.status === 'running' ? 'läuft' : entry.status === 'error' ? 'Fehler' : entry.status === 'cancelled' ? 'abgebrochen' : 'fertig'}
                  {entry.marked ? ' · Markiert' : ''}
                </p>
              </button>
              {open ? (
                <div className="mt-2 space-y-2">
                  {editingId === entry.id ? (
                    <textarea
                      data-testid="portal-hintergrund-editor"
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      rows={8}
                      className="w-full rounded-md border border-violet-400/30 bg-transparent px-2 py-1.5 text-[12px] leading-relaxed text-slate-100"
                      aria-label="Auswertung"
                    />
                  ) : (
                    <div
                      data-testid="portal-hintergrund-inhalt"
                      className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-md border border-white/[0.06] px-2 py-2 text-[12px] leading-relaxed text-slate-100"
                    >
                      {entry.text.trim() || 'Keine Auswertung.'}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      data-testid="portal-hintergrund-bearbeiten"
                      className={actionClass}
                      onClick={() => {
                        if (editingId === entry.id) {
                          persist(editHintergrundText(loadHintergrundMeta(customerId), entry.id, draft));
                          setEditingId(null);
                          return;
                        }
                        setDraft(entry.text);
                        setEditingId(entry.id);
                      }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      data-testid="portal-hintergrund-loeschen"
                      className={actionClass}
                      onClick={() => {
                        persist(deleteHintergrund(loadHintergrundMeta(customerId), entry.id));
                        jobs?.cancelJob(entry.id);
                        jobs?.dismissFinished(entry.id);
                        if (open) onOpenId(null);
                        if (editingId === entry.id) setEditingId(null);
                      }}
                    >
                      Löschen
                    </button>
                    <button
                      type="button"
                      data-testid="portal-hintergrund-markieren"
                      className={actionClass}
                      aria-pressed={entry.marked}
                      onClick={() => {
                        persist(toggleHintergrundMark(loadHintergrundMeta(customerId), entry.id, !entry.marked));
                      }}
                    >
                      Markieren
                    </button>
                    {chatAllowed ? (
                      <button
                        type="button"
                        data-testid="portal-hintergrund-in-chat"
                        className={actionClass}
                        onClick={() => {
                          if (!arkoExactFreigeschaltet(bausteinKategorien)) return;
                          const placed = placeAnalysisInArkoChat(customerId, entry.documentName, entry.text);
                          if (placed) onOpenArkoChat?.();
                        }}
                      >
                        In Chat öffnen
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </article>
          );
        })
      )}
    </div>
  );
};
